import type { LegendServiceType } from '@/features/jobs/legends/legend-service-type'
import { Id } from '@/infra/id'

/**
 * Links to routes.
 */
export const links = {
  home: () => '/',
  jobs: () => '/jobs',
  startNewService: () => '/new',
  startNewJob: (type: LegendServiceType) =>
    `/new?service_type=${mapServiceTypeToParam(type)}`,
  editDraftJob: (id: Id, options?: EditDraftJobOptions) => {
    const base = `/jobs/${Id.assert(id)}/onboarding`
    if (options) {
      const searchParams = mapEditDraftOptionsStepToSearchParams(options)
      return `${base}?${searchParams.toString()}`
    }

    return base
  },
  jobDetails: (id: Id, options?: { showExpectationsDialog?: boolean }) => {
    const base = `/jobs/${Id.assert(id)}`
    if (options?.showExpectationsDialog) {
      const searchParams = new URLSearchParams()
      searchParams.set('dialog', 'expectations')
      return `${base}?${searchParams.toString()}`
    }
    return base
  },
  jobDetailsMessages: (id: Id) => `${links.jobDetails(id)}/messages`,
  jobDetailsDocuments: (id: Id) => `${links.jobDetails(id)}/docs`,
  activity: () => '/activity',
  activityPreferences: () => '/activity/preferences',
  docs: () => '/docs',
  account: () => '/account',
  accountPersonalInfo: () => '/account/personal-info',
  accountBilling: () => '/account/billing',
  accountSecurity: () => '/account/security',
  emailConfirmed: () => '/email-confirmed',
  sessionExpired: () => '/__session/expired',
  requestMagicLink: () => '/request-magic-link',
  taxJobInfoGathering: (jobId: Id, selectedItemId?: Id) => {
    const base = `/jobs/${Id.assert(jobId)}/tax-info-gathering`
    if (selectedItemId) {
      return `${base}/${selectedItemId}`
    }
    return base
  },
  consultationJobInfoGathering: (jobId: Id) => {
    return `/jobs/${Id.assert(jobId)}/consultation-info-gathering`
  },
  taxPrepOrganizer: (jobId: Id) => {
    return `/jobs/${Id.assert(jobId)}/tax-prep-organizer`
  },
}

/**
 * External links.
 */
export const externalLinks = {
  contactSupport: () => 'https://www.taxfyle.com/contact-us',
  termsOfUse: () => 'https://www.taxfyle.com/policy/terms',
  privacyPolicy: () =>
    'https://www.taxfyle.com/policy/privacy-policy-january-10-2025',
}

/**
 * Maps a LegendServiceType to a string parameter for the URL.
 */
function mapServiceTypeToParam(type: LegendServiceType) {
  switch (type) {
    case 'Tax':
      return 'tax'
    case 'Bookkeeping':
      return 'bookkeeping'
    case 'Consultation':
      return 'consultation'
    case 'Misc':
      return 'misc'
  }
}

/**
 * Maps a service type param to a {@link LegendServiceType}.
 */
export function parseServiceTypeFromParams(
  params: URLSearchParams,
): LegendServiceType | null {
  const type = params.get('service_type')

  switch (type) {
    case 'tax':
      return 'Tax'
    case 'bookkeeping':
      return 'Bookkeeping'
    case 'consultation':
      return 'Consultation'
    case 'misc':
      return 'Misc'
    default:
      return null
  }
}

export interface EditDraftJobOptions {
  step: string
}

function mapEditDraftOptionsStepToSearchParams(
  options: EditDraftJobOptions,
): URLSearchParams {
  const params = new URLSearchParams()
  params.set('step', options.step)
  return params
}

/**
 * Parses search params to {@link EditDraftJobOptions}.
 *
 * @param params
 */
export function parseSearchParamsToEditDraftJobOptions(
  params: URLSearchParams,
): EditDraftJobOptions | null {
  const stepId = params.get('step')
  if (stepId) {
    return { step: stepId }
  }

  return null
}
